import { ValidationError } from '@lender/utils';
import _ from 'lodash';

import { DemographicInformationProvisionType, EthnicityType } from 'constants/loanApplication';
import { SEX_NOT_PROVIDED_VALUE } from 'constants/profile';
import { getConditionalArray, getConditionalObject } from 'utils/data';

import { DeclarationType, SEX_TYPE } from '../Declarations.constants';
import { customSetInForm } from '../Declarations.utils';

import { getModelPath } from './Demographic.config';

const VALIDATION_DELAY_MS = 200;
const DEBOUNCE_DELAY_MS = 500;

const requiredRaceCheckboxesErrorsObject = [
  'isAmericanIndianOrAlaskaNative',
  'isAsian',
  'isBlackOrAfricanAmerican',
  'isNativeHawaiianOrOtherPacificIslander',
  'isWhite',
  'isRaceNotProvided',
  'isRaceNotProvidedDuringFaceToFaceInterview',
  'raceLabel',
].reduce((acc, key) => {
  acc[key] = ValidationError.RequiredField;

  return acc;
}, {});

const checkIsSomeOfRestRaceFieldsChecked = ({
  isAmericanIndianOrAlaskaNative,
  isAsian,
  isBlackOrAfricanAmerican,
  isNativeHawaiianOrOtherPacificIslander,
  isWhite,
}) =>
  [
    isAmericanIndianOrAlaskaNative,
    isAsian,
    isBlackOrAfricanAmerican,
    isNativeHawaiianOrOtherPacificIslander,
    isWhite,
  ].some(Boolean);

const throttledSetValidationErrors = _.throttle(
  ({
    setValidationErrors,
    entityId,
    isHispanicOrLatino,
    isOtherHispanicOrLatinoEthnicity,
    isAmericanIndianOrAlaskaNative,
    isAsian,
    isOtherAsian,
    isNativeHawaiianOrOtherPacificIslander,
    isOtherPacificIslander,
    isFaceToFaceInterview,
    isSexInfoNotProvidedDuringFaceToFaceInterview,
    isEthnicityNotProvidedDuringFaceToFaceInterview,
    isSexSelected,
    isEthnicitySelected,
    areRaceCheckboxesRequired,
  }) => {
    setTimeout(
      () =>
        setValidationErrors(prev => {
          const {
            ethnicity: {
              isHispanicOrLatino: isHispanicOrLatinoError,
              isNonHispanicOrLatino,
              hispanicOrLatinoOptions,
              otherHispanicOrLatinoEthnicity,
            } = {},
            race: {
              americanTribeName,
              asianOptions,
              otherAsianRace,
              nativeHawaiianOrOtherPacificIslanderOptions,
              otherPacificIslander,
              ...restRaceErrors
            } = {},
            sex,
            isSexInfoNotProvidedDuringFaceToFaceInterview:
              isSexInfoNotProvidedDuringFaceToFaceInterviewError,
            ethnicityCollectedOnTheBasisOfVisualObservationOrSurname,
            sexCollectedOnTheBasisOfVisualObservationOrSurname,
            raceCollectedOnTheBasisOfVisualObservationOrSurname,
            ...restErrors
          } = prev[entityId]?.declarations?.demographicInformation ?? {};

          const { demographicInformation, ...restDeclarationsErrors } =
            prev[entityId]?.declarations ?? {};

          const sexError = !isFaceToFaceInterview
            ? sex
            : (!isSexInfoNotProvidedDuringFaceToFaceInterview && !isSexSelected && sex) || '';

          const ethnicityTypeError = !isFaceToFaceInterview
            ? ''
            : (!isEthnicityNotProvidedDuringFaceToFaceInterview &&
                !isEthnicitySelected &&
                isHispanicOrLatinoError &&
                isNonHispanicOrLatino) ||
              '';

          return {
            ...prev,
            [entityId]: {
              ...prev[entityId],
              declarations: {
                ...restDeclarationsErrors,

                demographicInformation: {
                  sex: sexError,
                  isSexInfoNotProvidedDuringFaceToFaceInterview: !isFaceToFaceInterview
                    ? ''
                    : sexError,
                  ethnicity: {
                    isHispanicOrLatino: ethnicityTypeError,
                    isNonHispanicOrLatino: ethnicityTypeError,

                    isEthnicityNotProvided: isFaceToFaceInterview ? '' : ethnicityTypeError,
                    isEthnicityNotProvidedDuringFaceToFaceInterview: !isFaceToFaceInterview
                      ? ''
                      : ethnicityTypeError,

                    ...getConditionalObject(isHispanicOrLatino, () => ({
                      hispanicOrLatinoOptions,

                      ...getConditionalObject(isOtherHispanicOrLatinoEthnicity, {
                        otherHispanicOrLatinoEthnicity,
                      }),
                    })),
                  },
                  race: {
                    ...getConditionalObject(isAmericanIndianOrAlaskaNative, {
                      americanTribeName,
                    }),
                    ...getConditionalObject(isAsian, () => ({
                      asianOptions,

                      ...getConditionalObject(isOtherAsian, {
                        otherAsianRace,
                      }),
                    })),

                    ...getConditionalObject(isNativeHawaiianOrOtherPacificIslander, () => ({
                      nativeHawaiianOrOtherPacificIslanderOptions,

                      ...getConditionalObject(isOtherPacificIslander, { otherPacificIslander }),
                    })),

                    ...getConditionalArray(areRaceCheckboxesRequired, restRaceErrors),
                  },
                  ...getConditionalArray(isFaceToFaceInterview, {
                    ethnicityCollectedOnTheBasisOfVisualObservationOrSurname,
                    sexCollectedOnTheBasisOfVisualObservationOrSurname,
                    raceCollectedOnTheBasisOfVisualObservationOrSurname,
                  }),

                  ...restErrors,
                },
              },
            },
          };
        }),
      VALIDATION_DELAY_MS,
    );
  },
  DEBOUNCE_DELAY_MS,
  { leading: true, trailing: false },
);

export const normalizeForm = ({ form, entityId, setValidationErrors, entityData }) => {
  const {
    ethnicity = {},
    race = {},
    sex,
    demographicInformationProvisionType,
    ethnicityCollectedOnTheBasisOfVisualObservationOrSurname,
    sexCollectedOnTheBasisOfVisualObservationOrSurname,
    raceCollectedOnTheBasisOfVisualObservationOrSurname,
    isSexInfoNotProvidedDuringFaceToFaceInterview,
  } = form[entityId]?.declarations?.demographicInformation ?? {};

  const {
    hispanicOrLatinoOptions = {},
    otherHispanicOrLatinoEthnicity,
    isEthnicityNotProvidedDuringFaceToFaceInterview,
    isHispanicOrLatino,
    isNonHispanicOrLatino,
    isEthnicityNotProvided,
  } = ethnicity;

  const { isOtherHispanicOrLatinoEthnicity } = hispanicOrLatinoOptions;

  const {
    raceLabel,
    isAmericanIndianOrAlaskaNative,
    americanTribeName,
    isAsian,
    asianOptions = {},
    otherAsianRace,
    isBlackOrAfricanAmerican,
    isNativeHawaiianOrOtherPacificIslander,
    nativeHawaiianOrOtherPacificIslanderOptions = {},
    isWhite,
    isRaceNotProvided,
    isRaceNotProvidedDuringFaceToFaceInterview,
    otherPacificIslander,
  } = race;

  const { isOtherAsian } = asianOptions;
  const { isOtherPacificIslander } = nativeHawaiianOrOtherPacificIslanderOptions;

  const isFaceToFaceInterview =
    demographicInformationProvisionType ===
    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

  const faceToFaceSexValue = sex !== SEX_NOT_PROVIDED_VALUE ? sex : null;
  const sexValue = isFaceToFaceInterview
    ? faceToFaceSexValue
    : (sex ?? entityData?.declarations?.demographicInformation?.rawSex);

  const initialData = entityData?.declarations?.demographicInformation ?? {};

  const areRaceCheckboxesRequired = checkAreRaceCheckboxesRequired({
    isAmericanIndianOrAlaskaNative,
    isAsian,
    isBlackOrAfricanAmerican,
    isNativeHawaiianOrOtherPacificIslander,
    isWhite,
    isRaceNotProvided: isRaceNotProvided ?? initialData.race?.isRaceNotProvided,
    isRaceNotProvidedDuringFaceToFaceInterview:
      isRaceNotProvidedDuringFaceToFaceInterview ??
      initialData.race?.isRaceNotProvidedDuringFaceToFaceInterview,
    isFaceToFaceInterview,
  });

  throttledSetValidationErrors({
    setValidationErrors,
    entityId,
    isHispanicOrLatino,
    isOtherHispanicOrLatinoEthnicity,
    isAmericanIndianOrAlaskaNative,
    isAsian,
    isOtherAsian,
    isNativeHawaiianOrOtherPacificIslander,
    isOtherPacificIslander,
    isFaceToFaceInterview,
    isSexInfoNotProvidedDuringFaceToFaceInterview:
      isSexInfoNotProvidedDuringFaceToFaceInterview ??
      initialData.isSexInfoNotProvidedDuringFaceToFaceInterview,
    isEthnicityNotProvidedDuringFaceToFaceInterview:
      isEthnicityNotProvidedDuringFaceToFaceInterview ??
      initialData.ethnicity?.isEthnicityNotProvidedDuringFaceToFaceInterview,
    isSexSelected: !!sexValue,
    isEthnicitySelected: !!(
      isHispanicOrLatino ||
      isNonHispanicOrLatino ||
      (isEthnicityNotProvided && null)
    ),

    areRaceCheckboxesRequired,
  });

  return {
    ...form,
    [entityId]: {
      ...form[entityId],
      declarations: {
        ...form[entityId]?.declarations,
        demographicInformation: {
          ethnicity: {
            isHispanicOrLatino,
            isNonHispanicOrLatino,

            ...getConditionalObject(!isFaceToFaceInterview, {
              isEthnicityNotProvided,
            }),

            ...getConditionalObject(isHispanicOrLatino, () => ({
              hispanicOrLatinoOptions,

              ...getConditionalObject(isOtherHispanicOrLatinoEthnicity, {
                otherHispanicOrLatinoEthnicity,
              }),
            })),

            ...getConditionalObject(isFaceToFaceInterview, {
              isEthnicityNotProvidedDuringFaceToFaceInterview,
            }),
          },

          sex: sexValue,
          ...getConditionalObject(isFaceToFaceInterview, {
            isSexInfoNotProvidedDuringFaceToFaceInterview,
          }),

          race: {
            raceLabel,
            isAmericanIndianOrAlaskaNative,
            ...getConditionalObject(isAmericanIndianOrAlaskaNative, {
              americanTribeName,
            }),

            isAsian,
            ...getConditionalObject(isAsian, () => ({
              asianOptions,

              ...getConditionalObject(isOtherAsian, {
                otherAsianRace,
              }),
            })),

            isBlackOrAfricanAmerican,
            isNativeHawaiianOrOtherPacificIslander,
            ...getConditionalObject(isNativeHawaiianOrOtherPacificIslander, () => ({
              nativeHawaiianOrOtherPacificIslanderOptions,

              ...getConditionalObject(isOtherPacificIslander, { otherPacificIslander }),
            })),

            isWhite,

            ...(isFaceToFaceInterview
              ? {
                  isRaceNotProvidedDuringFaceToFaceInterview,
                }
              : {
                  isRaceNotProvided,
                }),
          },

          demographicInformationProvisionType,
          ...getConditionalObject(isFaceToFaceInterview, {
            ethnicityCollectedOnTheBasisOfVisualObservationOrSurname,
            sexCollectedOnTheBasisOfVisualObservationOrSurname,
            raceCollectedOnTheBasisOfVisualObservationOrSurname,
          }),
        },
      },
    },
  };
};

export const checkAreRaceCheckboxesRequired = ({
  isAmericanIndianOrAlaskaNative,
  isAsian,
  isBlackOrAfricanAmerican,
  isNativeHawaiianOrOtherPacificIslander,
  isWhite,
  isRaceNotProvided,
  isRaceNotProvidedDuringFaceToFaceInterview,
  isFaceToFaceInterview,
}) =>
  [
    isAmericanIndianOrAlaskaNative,
    isAsian,
    isBlackOrAfricanAmerican,
    isNativeHawaiianOrOtherPacificIslander,
    isWhite,
    isFaceToFaceInterview ? isRaceNotProvidedDuringFaceToFaceInterview : isRaceNotProvided,
  ].every(val => !val);

export const customOnChangeRaceInput = ({
  e,
  value,
  setInForm,
  entityId,
  setForm,
  setValidationErrors,
}) => {
  const currentFieldName = e.target.name;

  const nameInRaceModel = currentFieldName.split('.').slice(-1)[0];

  const customAction = (sectionForm = {}) => {
    const { demographicInformationProvisionType } = sectionForm;

    const isFaceToFaceInterview =
      demographicInformationProvisionType ===
      DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

    setForm(prev => {
      const raceFieldsToSet =
        nameInRaceModel === 'isRaceNotProvided' && value
          ? {
              raceLabel: undefined,
              isAmericanIndianOrAlaskaNative: false,
              isAsian: false,
              isBlackOrAfricanAmerican: false,
              isNativeHawaiianOrOtherPacificIslander: false,
              isWhite: false,
              isRaceNotProvided: true,
            }
          : {
              ...prev[entityId]?.declarations?.demographicInformation?.race,
              ...getConditionalObject(!isFaceToFaceInterview, {
                isRaceNotProvided: false,
              }),
              [nameInRaceModel]: value,
            };

      const areRaceCheckboxesRequired = checkAreRaceCheckboxesRequired({
        ...raceFieldsToSet,
        isFaceToFaceInterview,
      });

      setValidationErrors(prev => {
        const {
          isAmericanIndianOrAlaskaNative,
          isAsian,
          isBlackOrAfricanAmerican,
          isNativeHawaiianOrOtherPacificIslander,
          isWhite,
          isRaceNotProvided,
          isRaceNotProvidedDuringFaceToFaceInterview,
          raceLabel,
          ...restErrors
        } = prev[entityId]?.declarations?.demographicInformation?.race ?? {};

        return {
          ...prev,
          [entityId]: {
            ...prev[entityId],
            declarations: {
              ...prev[entityId]?.declarations,
              demographicInformation: {
                ...prev[entityId]?.declarations?.demographicInformation,
                race: {
                  ...restErrors,

                  ...getConditionalObject(
                    areRaceCheckboxesRequired,
                    requiredRaceCheckboxesErrorsObject,
                  ),
                },
              },
            },
          },
        };
      });

      return {
        ...prev,
        [entityId]: {
          ...prev[entityId],
          declarations: {
            ...prev[entityId]?.declarations,
            demographicInformation: {
              ...prev[entityId]?.declarations?.demographicInformation,
              race: raceFieldsToSet,
            },
          },
        },
      };
    });
  };

  customSetInForm({
    declarationType: DeclarationType.DEMOGRAPHIC_INFORMATION,
    fieldName: currentFieldName,
    value,
    entityId,
    setForm,
    setInForm,
    customAction,
  });
};

export const customOnChangeEthnicityFields = ({
  e,
  value,
  setInForm,
  entityId,
  setForm,
  staticValidateField,
  flagName,
  setIsTouched,
}) => {
  const currentFieldName = e.target.name;

  const isCheckboxAction = flagName === 'isEthnicityNotProvidedDuringFaceToFaceInterview';

  const customAction = (form = {}) => {
    const sectionFormRef = { current: form };

    const { demographicInformationProvisionType, ethnicity } = sectionFormRef.current;

    const {
      isHispanicOrLatino,
      isNonHispanicOrLatino,
      isEthnicityNotProvided,
      ...restEthnicityFields
    } = ethnicity;

    const isFaceToFaceInterview =
      demographicInformationProvisionType ===
      DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

    const options = Object.values(EthnicityType).slice(0, isCheckboxAction ? 2 : Infinity);

    if (!isCheckboxAction) {
      setForm(prev => {
        const updatedForm = {
          ...prev,
          [entityId]: {
            ...prev[entityId],
            declarations: {
              ...prev[entityId]?.declarations,
              demographicInformation: {
                ...prev[entityId]?.declarations?.demographicInformation,
                ethnicity: options.reduce(
                  (acc, curFlagName) => {
                    acc[curFlagName] = curFlagName === flagName;

                    return acc;
                  },
                  {
                    ...restEthnicityFields,
                  },
                ),
              },
            },
          },
        };

        sectionFormRef.current = updatedForm[entityId]?.declarations?.demographicInformation;

        return updatedForm;
      });

      setIsTouched(true);
    }

    const fieldsToValidate = [
      ...options,
      ...getConditionalArray(isFaceToFaceInterview, [
        'isEthnicityNotProvidedDuringFaceToFaceInterview',
      ]),
    ];

    fieldsToValidate.forEach(fieldName => {
      const { [fieldName]: fieldValueToValidate } = sectionFormRef.current.ethnicity;

      const fieldNameToValidate = `${getModelPath(entityId)}ethnicity.${fieldName}`;

      setTimeout(
        () =>
          staticValidateField(
            fieldNameToValidate,
            fieldNameToValidate === currentFieldName ? value : fieldValueToValidate,
          ),
        VALIDATION_DELAY_MS,
      );
    });
  };

  customSetInForm({
    declarationType: DeclarationType.DEMOGRAPHIC_INFORMATION,
    fieldName: currentFieldName,
    value,
    entityId,
    setForm,
    setInForm,
    customAction,
    blank: !isCheckboxAction,
  });
};

export const customOnChangeEthnicityOrSexFields = async ({
  e,
  value,
  setInForm,
  entityId,
  setForm,
  staticValidateField,
  type = SEX_TYPE,
}) => {
  const currentFieldName = e.target.name;

  const customAction = (sectionForm = {}) => {
    const { demographicInformationProvisionType } = sectionForm;

    const isFaceToFaceInterview =
      demographicInformationProvisionType ===
      DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

    const fieldsToValidate =
      type === SEX_TYPE
        ? [
            'sex',
            ...getConditionalArray(isFaceToFaceInterview, [
              'isSexInfoNotProvidedDuringFaceToFaceInterview',
            ]),
          ]
        : [
            'type',
            ...getConditionalArray(isFaceToFaceInterview, [
              'isEthnicityNotProvidedDuringFaceToFaceInterview',
            ]),
          ];

    const sectionScope = type === SEX_TYPE ? sectionForm : sectionForm.ethnicity;

    fieldsToValidate.forEach(fieldName => {
      const { [fieldName]: fieldValueToValidate } = sectionScope;

      const fieldNameToValidate =
        type === SEX_TYPE
          ? `${getModelPath(entityId)}${fieldName}`
          : `${getModelPath(entityId)}ethnicity.${fieldName}`;

      setTimeout(
        () =>
          staticValidateField(
            fieldNameToValidate,
            fieldNameToValidate === currentFieldName ? value : fieldValueToValidate,
          ),
        VALIDATION_DELAY_MS,
      );
    });
  };

  customSetInForm({
    declarationType: DeclarationType.DEMOGRAPHIC_INFORMATION,
    fieldName: currentFieldName,
    value,
    entityId,
    setForm,
    setInForm,
    customAction,
  });
};

export const customOnChangeDemographicInformationProvisionType = async ({
  e,
  value,
  setInForm,
  entityId,
  setForm,
  validateField,
}) => {
  const currentFieldName = e.target.name;

  const customAction = (sectionForm = {}) => {
    const { demographicInformationProvisionType: prevValue, race = {} } = sectionForm;

    const isFaceToFaceInterview =
      value === DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

    validateField(currentFieldName, value);

    if (
      prevValue !== DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW &&
      !isFaceToFaceInterview
    ) {
      return;
    }

    setForm(prev => ({
      ...prev,
      [entityId]: {
        ...prev[entityId],
        declarations: {
          ...prev[entityId]?.declarations,
          demographicInformation: {
            ...prev[entityId]?.declarations?.demographicInformation,
            race: {
              ...race,
              ...(isFaceToFaceInterview
                ? { isRaceNotProvidedDuringFaceToFaceInterview: race.isRaceNotProvided }
                : {
                    isRaceNotProvided: checkIsSomeOfRestRaceFieldsChecked(race)
                      ? false
                      : race.isRaceNotProvidedDuringFaceToFaceInterview,
                  }),
            },
          },
        },
      },
    }));
  };

  customSetInForm({
    declarationType: DeclarationType.DEMOGRAPHIC_INFORMATION,
    fieldName: currentFieldName,
    value,
    entityId,
    setForm,
    setInForm,
    customAction,
  });
};
